import React from 'react';
import type {ReactBasicCompositionProps} from '@wix/devzai-utils-react';
import {reactCreateMemo} from '@wix/devzai-utils-react';
import {classes, style, vars} from './bymo-page-root.st.css';
import {
    bymoPageHasNavigationMenuItems,
    BymoPagePropsForViewer,
    BymoPageTextSize,
    BymoTemplateLayout,
    BymoTemplateStandardDesktopLayoutContentWidth
} from "../../client-server-common";
import type {Values} from "@wix/devzai-utils-common";
import {cssAddUnitsToNumericValue, enumValueOrDefault, evaluateFunction} from "@wix/devzai-utils-common";
import {
    BymoPageLayoutSizes,
    bymoPageStandardDesktopLayoutGetContentWidthPixels
} from "../bymo-page-style/bymo-page-style";
import {
    BymoTemplateSpeedDialMenuAppearance
} from '../bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial-common';

const StandardDesktopLayoutContentWidthCssState = {
    Tiny: 'tiny',
    Small: 'small',
    Medium: 'medium',
    Large: 'large',
    FullWidth: 'fullWidth',
} as const;

type StandardDesktopLayoutContentWidthCssState = Values<typeof StandardDesktopLayoutContentWidthCssState>;


const TextSizeCssState = {
    Zero: 'zero',

    Small: 'small',
    Medium: 'medium',
} as const;

type TextSizeCssState = Values<typeof TextSizeCssState>;

const BymoPageLayoutGroup = {
    Unspecified: 'unspecified',
    Group1: 'group1',
    Group2: 'group2',
    Group3: 'group3',
    Group4: 'group4',
    Group5: 'group5',
} as const;

type BymoPageLayoutGroup = Values<typeof BymoPageLayoutGroup>;

export const BymoPageRoot = reactCreateMemo(function BymoPageRoot (props: BymoPageRoot.Props) {
    const {
        children,
        bymoPageProps,
        rootRef,

        style: styleProp,
        className,
        ...htmlAttributes
    } = props;

    const standardDesktopLayoutContentWidth = enumValueOrDefault(BymoTemplateStandardDesktopLayoutContentWidth, bymoPageProps.standardDesktopLayoutContentWidth, BymoTemplateStandardDesktopLayoutContentWidth.Tiny);
    const textSize = enumValueOrDefault(BymoPageTextSize, bymoPageProps.textSize, BymoPageTextSize.Small);

    const hasMenuHeader =
        bymoPageProps.menuAppearance !== BymoTemplateSpeedDialMenuAppearance.Hidden &&
        bymoPageHasNavigationMenuItems(bymoPageProps);

    return (
        <div
            {...htmlAttributes}
            ref={rootRef}
            style={{
                ...styleProp,
                [vars.centeredContentSectionsMaxWidth]: cssAddUnitsToNumericValue(BymoPageLayoutSizes.WideLayoutCenteredContentSectionsMaxWidth, 'px'),
                [vars.contentWidthInWideLayout]: cssAddUnitsToNumericValue(evaluateFunction(() => {
                    if (standardDesktopLayoutContentWidth === BymoTemplateStandardDesktopLayoutContentWidth.FullWidth) {
                        return 0;
                    } else {
                        return bymoPageStandardDesktopLayoutGetContentWidthPixels(standardDesktopLayoutContentWidth);
                    }
                }), 'px'),
            }}
            className={style(classes.root, {
                layoutGroup: evaluateFunction<BymoPageLayoutGroup>(() => {
                    switch (bymoPageProps.pageLayout.layout) {
                        case BymoTemplateLayout.Layout9:
                        case BymoTemplateLayout.Layout10:
                        case BymoTemplateLayout.Layout11:
                        case BymoTemplateLayout.Layout8: {
                            return BymoPageLayoutGroup.Group1;
                        }
                        case BymoTemplateLayout.Layout14:
                        case BymoTemplateLayout.Layout7: {
                            return BymoPageLayoutGroup.Group2;
                        }
                        case BymoTemplateLayout.Layout2:
                        case BymoTemplateLayout.Layout4:
                        case BymoTemplateLayout.Layout5:
                        case BymoTemplateLayout.Layout13: {
                            return BymoPageLayoutGroup.Group3;
                        }
                        case BymoTemplateLayout.Layout3:
                        case BymoTemplateLayout.Layout1:
                        case BymoTemplateLayout.Standard:
                        case BymoTemplateLayout.Blank: {
                            return BymoPageLayoutGroup.Group4;
                        }
                        case BymoTemplateLayout.Layout6:
                        case BymoTemplateLayout.Layout12: {
                            return BymoPageLayoutGroup.Group5;
                        }
                    }
                }),
                bymoPageContentSectionsAreCenteredInContentOnDesktop: evaluateFunction(() => {
                    switch (standardDesktopLayoutContentWidth) {
                        case BymoTemplateStandardDesktopLayoutContentWidth.Tiny:
                        case BymoTemplateStandardDesktopLayoutContentWidth.Small: {
                            return false;
                        }
                        case BymoTemplateStandardDesktopLayoutContentWidth.Medium:
                        case BymoTemplateStandardDesktopLayoutContentWidth.Large:
                        case BymoTemplateStandardDesktopLayoutContentWidth.FullWidth: {
                            return true;
                        }
                    }
                }),
                standardDesktopLayoutContentWidth: evaluateFunction<StandardDesktopLayoutContentWidthCssState>(() => {
                    switch (standardDesktopLayoutContentWidth) {
                        case BymoTemplateStandardDesktopLayoutContentWidth.Tiny: return StandardDesktopLayoutContentWidthCssState.Tiny;
                        case BymoTemplateStandardDesktopLayoutContentWidth.Small: return StandardDesktopLayoutContentWidthCssState.Small;
                        case BymoTemplateStandardDesktopLayoutContentWidth.Medium: return StandardDesktopLayoutContentWidthCssState.Medium;
                        case BymoTemplateStandardDesktopLayoutContentWidth.Large: return StandardDesktopLayoutContentWidthCssState.Large;
                        case BymoTemplateStandardDesktopLayoutContentWidth.FullWidth: return StandardDesktopLayoutContentWidthCssState.FullWidth;
                    }
                }),
                textSize: evaluateFunction<TextSizeCssState>(() => {

                    if (bymoPageProps.devApplyZeroValueTextSize) {
                        return TextSizeCssState.Zero;
                    }

                    switch (textSize) {
                        case BymoPageTextSize.Small: return TextSizeCssState.Small;
                        case BymoPageTextSize.Medium: return TextSizeCssState.Medium;
                    }
                }),
                hasHeaderMenu: hasMenuHeader
            }, className)}
        >
            {children}
        </div>
    );
});

export namespace BymoPageRoot {

    export interface Props extends ReactBasicCompositionProps {
        bymoPageProps: BymoPagePropsForViewer;
        rootRef?: React.Ref<HTMLDivElement>;
    }
}
